import { SpinResponse, InternalRespinSpecGroup, FreeSpinsInfoEvent } from '@cgs/data-access';
import { Container } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { GameStateMachine } from '../../reels_engine/state_machine/game-state-machine';
import { BaseListener } from './base_listener';
import { GameStateMachineStates } from '../../reels_engine/state_machine/game-state-machine-states';
import { SLOT_SESSION_TOKEN, SlotSession } from './slot_session';

export interface SpinViewController {
  enableStopButtonOnAccelerate(): void;
  decreaseBalanceAndStopUpdate(): void;
  verifyBalance(): void;
  enableStopButton(): void;
  disableSpinButton(): void;
  disableStopButton(): void;
  checkAutoSpinCondition(): void;
  updateBalance(): void;
  enableSpins(): void;
}

export class SpinViewMachineListener extends BaseListener<any> {
  public viewController: SpinViewController;

  private readonly _gameStateMachine: GameStateMachine<SpinResponse>;

  constructor(container: Container) {
    super(container);
    this._gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    this._slotSession = container.resolve<SlotSession>(SLOT_SESSION_TOKEN);
  }

  public onStateEntered(slotState: string): void {
    switch (slotState) {
      case GameStateMachineStates.Accelerate:
        this.onAccelerateEntered();
        break;
      case GameStateMachineStates.Stopping:
        this.onStoppingEntered();
        break;
      case GameStateMachineStates.Collapse:
        this.onCollapseStateEntered();
        break;
      case GameStateMachineStates.ImmediatelyStop:
        this.onImmediatelyStopEntered();
        break;
      case GameStateMachineStates.Stop:
        this.onStopEntered();
        break;
      case GameStateMachineStates.Bonus:
        this.onBonusEntered();
        break;
      default:
        break;
    }
  }

  public onStateExited(slotState: string): void {
    switch (slotState) {
      case GameStateMachineStates.Bonus:
        this.onBonusExited();
        break;
      case GameStateMachineStates.Animation:
        this.onAnimationExit();
        this.onVerifyBalanceEntered();
        break;
      default:
        break;
    }
  }

  private onAccelerateEntered(): void {
    if (!this._gameStateMachine.curResponse.isRespin) {
      if (
        this._gameStateMachine.isAutoSpins &&
        (!this._gameStateMachine.curResponse.isFreeSpins ||
          this._gameStateMachine.curResponse.freeSpinsInfo!.event ==
            FreeSpinsInfoEvent.FreeSpinsFinished)
      ) {
        this.viewController.enableStopButtonOnAccelerate();
      }
      this.viewController.decreaseBalanceAndStopUpdate();
    }
  }

  private onVerifyBalanceEntered(): void {
    this.viewController.verifyBalance();
  }

  private onStoppingEntered(): void {
    this.viewController.enableStopButton();
  }

  private onCollapseStateEntered(): void {
    this.viewController.disableSpinButton();
  }

  private onImmediatelyStopEntered(): void {
    this.viewController.disableSpinButton();
  }

  private onStopEntered(): void {
    if (this._gameStateMachine.curResponse.isFreeSpins) {
      if (
        this._gameStateMachine.curResponse.freeSpinsInfo!.event ==
        FreeSpinsInfoEvent.FreeSpinsFinished
      ) {
        this.viewController.disableSpinButton();
      } else if (
        this._gameStateMachine.curResponse.freeSpinsInfo!.event ==
          FreeSpinsInfoEvent.FreeSpinsStarted &&
        this._gameStateMachine.curResponse.isRespin
      ) {
        this.viewController.enableStopButtonOnAccelerate();
      } else {
        this.viewController.disableStopButton();
      }
    }

    const respinGroup =
      this._gameStateMachine.curResponse.additionalData instanceof InternalRespinSpecGroup
        ? (this._gameStateMachine.curResponse.additionalData as InternalRespinSpecGroup)
        : null;

    if (respinGroup && respinGroup.respinCounter < respinGroup.groups.length - 1) {
      if (this._gameStateMachine.isAutoSpins) {
        this.viewController.enableStopButtonOnAccelerate();
      } else {
        this.viewController.disableStopButton();
      }
    }

    this.viewController.checkAutoSpinCondition();
  }

  private onAnimationExit(): void {
    this.viewController.updateBalance();
  }

  private onBonusEntered(): void {
    this.viewController.disableSpinButton();
  }

  private onBonusExited(): void {
    this.viewController.enableSpins();
  }
}
